import { useEffect, useRef, useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import {
  listTaskDocs,
  createTaskDoc,
  updateTaskDoc,
  deleteTaskDoc,
  listAgendaEventDocs,
  createAgendaEventDoc,
  updateAgendaEventDoc,
  deleteAgendaEventDoc,
} from "../api";
import { useConfirmAndUndo, UndoToast } from "./useConfirmAndUndo";
import { useTranslation } from "../i18n";
// 151 §2 step 4 — case_stage, for the select's labels; 151C §5 — the two
// classify feeds (document_category tuples · document_status vlc), the same
// module-cache subscriptions App holds, rather than threading through six
// drawer hosts that never held them.
import { useTaxonomy, useTaxonomyList } from "../hooks/useTaxonomy";
import { openDocHref, AddButton } from "./SharedUI";
import DocumentEntryModal, {
  type DocEntryDraft,
  type StageOption,
} from "./modals/DocumentEntryModal"; // 151 §2 — the one modal
// 151C §6 — the purple-link read/write the Link to Stage select rides now.
import { directLinkedStageId, reconcileStageLink } from "../lib/stageItemLink";
import { isEmailDocUrl, openEmailDocHref } from "../lib/emailLink"; // 126 §9
// 151C §8 (a) — the FILE-TYPE badge on the row (the stage row's own), with
// its feeds; the generic lucide icon serves only the drawer toggle now.
import { DocTypeBadge } from "./DocTypeBadge";
import { FT_INFO, detFT, I } from "../utils";
import { RAD, FS } from "../lib/tokens";
/* BRIEF 95 §3a(e) — THE ADD ROW'S COLOUR, READ OFF THE ADD-SUBTASK ROW.
   §3a(e) asks for "the one set in Settings, the same one brief 92 §3d gave the
   subtask add row", and forbids naming a colour here. So this is an IMPORT of
   the very object that row spreads, not a copy of its value: the two rows
   cannot drift, and the token behind it (`--bi` at 55%) is stated in exactly
   one place. Measured: both rows sit on `tr.subtaskDrawer` in the drawer and on
   the modal's own surface in the modal, so the translucent fill composites to
   the same colour on both — they do NOT resolve differently, which is the
   answer §3a(e) asks for either way. */

// Phase 29 round 13 (C72) — task DOCUMENTS subpane, modeled on the subtask
// pane: one row per document —
//   [FileText icon → opens the link (new tab, guarded by openDocHref's
//    invalid/not-allowed toasts, stopPropagation)] [label — truncating]
//   [archive toggle]
// — plus a "+ Add" row (label + URL) underneath. No hard delete from the
// pane: archive dims the row in place and the same control restores it.
//
// Two modes, exactly like SubtaskChecklist:
//   • persisted (taskId set): loads/persists through /api/task-docs and
//     lifts every change up via onDocsChange so tk[i].docs stays fresh;
//   • local (no taskId — the create modal): rows live in the parent form
//     and flush server-side with the task create (onLocalChange).
//
// `canWrite` carries the C72 rights decision from the caller: any assignee,
// explicit or delegation-induced, plus the usual edit-grant holders, may
// add and archive/restore. Viewing/opening is never gated here.
//
// ── BRIEF 87 §4 — IT SERVES AGENDA EVENTS TOO, THROUGH ONE PARAMETER ────────
//
// Brief 87 §0(3) rules that the EVENT modal gains the TASK modal's Documents
// section and NEVER the reverse, and that "the value is that the two match, not
// that one is improved". A second pane would have been two implementations of
// one thing on day one — brief 59's finding, written deliberately.
//
// So this pane takes `eventId` as the alternative to `taskId` and swaps FOUR
// FUNCTIONS AND ONE PAYLOAD KEY. Nothing else differs, because nothing else
// differed on the server either: routes/agendaEventDocs.js is C72's mirror.
// Every existing task call site passes `taskId` and is untouched by construction
// — the task branch is the default in each of the five places the kind is read.

// 109 §1b — the row the edit dialog corrects. `_localId` is the create-modal's
// unsaved-row identity; `id` the persisted one.
type DocRowShape = {
  id?: number;
  _localId?: string;
  label?: string | null;
  url?: string;
  archived?: boolean;
  // 151C §5 — the classify fields the store now holds.
  fileType?: string | null;
  date?: string | null;
  type?: string | null;
  status?: string | null;
  note?: string | null;
};

type TaskDocsPaneProps = {
  taskId?: any;
  // §4 — set INSTEAD of taskId. Exactly one of the two is expected; taskId wins
  // if both arrive, so a caller cannot accidentally address both parents.
  eventId?: number | null;
  initialDocs?: any;
  onDocsChange?: (next: any) => void;
  onLocalChange?: (next: any) => void;
  canWrite?: boolean;
  /* 110 §1a — the matter binding for the OneDrive picker; absent = no button.
     The stage drawer's shape (CaseStageTimeline `oneDrive`), replacing 91
     §1b's `addRowSlot` → `OneDriveDocButton` chain: that closure APPENDED a
     row through the host's own write and clamped the picker to the matter's
     `caseRootFolder` as `basePath` — a folder that fails to load leaves a
     browser whose "Up" is disabled at its own root, which is Laurent's
     "without the possibility to browse entirely". The button now lives in the
     dialog itself (add AND edit), the pick only FILLS the draft (url + label
     from the file name), and the user still presses Add/Save — so the pane
     observes the whole flow and confinement stays the server's job
     (caseId/ccId, Phase 23), exactly as the stage drawer does it. */
  oneDrive?: { caseId?: number | string | null; ccId?: number | string | null } | null;
  /* 109 §1b — the HOST's lock state. Locked (the default): a chip click opens
     the link, exactly today's behaviour. Unlocked + write rights: the click
     reopens the add dialog PREFILLED and Save corrects the row in place — a
     bad URL was previously unfixable except by archive-and-recreate. The two
     modals pass false (always edit-context); the drawer hosts pass their
     section lock; a host with no lock affordance (Dashboard) leaves the
     default and keeps today's open-on-click. */
  locked?: boolean;
  /* ── 151C §6 — THE STAGE LINK IS THE PURPLE LINK (overrules 151 §2 step 4's
     stageId pen). The dialog's Link to Stage now reads and writes the
     STANDARD task/event↔stage itemLink — the links system's own row, drawn
     in --hll and unlinkable in the usual places (LinksManagerDialog) — and
     never the parent's stageId, whose amber-flashing pill was the retired
     mechanism's tell. The host hands the MATTER BINDING and the pane owns
     the write (lib/stageItemLink.ts): the server dedupes creates and its
     link pokes refetch the matter row for every open tab, sender included,
     so no host pen and no patcher exist to drift. Absent = no select — a
     matterless host, and a CREATE modal whose parent has no id yet, have
     nothing to link.
     (v)'s shape survives on the new store: an ADD with a stage chosen
     creates the link, an add that chose none never removes one. (vi)'s too:
     an EDIT that changes the stage moves the link, the modal's purple
     Unlink clears it at save. */
  stageLink?: {
    stages: ReadonlyArray<StageOption>;
    matterKind: "ca" | "cc";
    matterId: number;
    matterRow?: { itemLinks?: unknown } | null;
  } | null;
};

export default function TaskDocsPane({
  taskId,
  eventId,
  initialDocs,
  onDocsChange,
  onLocalChange,
  canWrite = false,
  oneDrive,
  locked = true,
  stageLink,
}: TaskDocsPaneProps) {
  const { t } = useTranslation();
  /* 151 §2 step 4 — the select's labels resolve stage type codes; own
     subscription (module cache — the same feed every view holds) rather than
     a threading through six drawer hosts that never held it. */
  const _stageEntries = useTaxonomy("case_stage").entries;
  /* 151C §5 — one modal means ONE FACE: the classify fields render from every
     entry point now that the store holds them. The two lists are App's own
     feeds by construction (the same useTaxonomyList calls App.tsx makes). */
  const _docTypesList = useTaxonomyList("document_category");
  const _docStatusesList = useTaxonomyList("document_status");
  /* the five classify fields as the wire writes them — empty → null, one
     builder for create, patch, local rows and the bin's undo re-create */
  const draftClassify = (d: DocEntryDraft) => ({
    fileType: d.fileType || null,
    date: d.date || null,
    type: d.type || null,
    status: d.status || null,
    note: d.note || null,
  });
  // §4 — the kind, decided once. `isEvent` is false whenever a taskId is present,
  // so the task path stays the default at every site below.
  const isEvent = taskId == null && eventId != null;
  const parentId = isEvent ? eventId : taskId;
  const persisted = parentId != null;
  const _list = isEvent ? listAgendaEventDocs : listTaskDocs;
  const [items, setItems] = useState<any[]>(() =>
    Array.isArray(initialDocs) ? initialDocs : [],
  );
  const [loading, setLoading] = useState(false);
  /* BRIEF 108 §1 — the permanent add row became a small modal behind a
     "+ Add" on the heading line. The draft survives as the modal's form.
     109 §1b — the same dialog serves EDIT: `editRow` holds the row being
     corrected (null = adding), the draft prefills from it, and Save goes
     through the PATCH instead of the create. */
  const [addOpen, setAddOpen] = useState(false);
  const [editRow, setEditRow] = useState<DocRowShape | null>(null);
  /* 151 §2 — the draft is LIFTED into this pane (the one modal's lifted-
     draft arm): 127 §1's upload fills it from outside the dialog. Shape is
     the modal's own; step 4 adds the stageId the Link to Stage select
     edits, prefilled from the parent's current link at open. */
  const [draft, setDraft] = useState<DocEntryDraft>({ name: "", link: "" });
  /* 151C §6 — the parent as a link ref, and what the select shows: the
     parent's DIRECT purple stage partner (never the closure walk). */
  const linkRef = persisted
    ? ({ k: isEvent ? "event" : "task", id: Number(parentId) } as const)
    : null;
  const linkedStageId =
    stageLink && linkRef ? directLinkedStageId(stageLink.matterRow, linkRef) : null;
  /* (v)/(vi) on the purple store — one writer for both save paths: fires
     only when the dialog's choice DIFFERS from the current direct link, and
     on ADD never in the removing direction ((v) is create-only; an add that
     left the select empty must not unlink a task somebody linked by hand).
     Fire-and-forget with a toast on failure: the doc write already
     succeeded, and the link's state comes back over the poke either way. */
  const writeStageLink = (mode: "add" | "edit") => {
    if (!stageLink || !linkRef) return;
    const chosen = draft.stageId ?? null;
    if (chosen === linkedStageId) return;
    if (mode === "add" && chosen == null) return;
    void reconcileStageLink({
      matterKind: stageLink.matterKind,
      matterId: stageLink.matterId,
      matterRow: stageLink.matterRow,
      item: linkRef,
      stageId: chosen,
    }).catch((e) => {
      window.__lxToast?.(
        "Link: " + (e instanceof Error ? e.message : "failed"),
        "error",
      );
    });
  };
  /* 151 §3 — 127 §1's upload machinery (hidden input · fileIntoMatter · the
     127 §2 conflict dialog) MOVED into DocumentEntryModal, one implementation
     for every variant; this pane passes `upload` and keeps nothing of it. */

  // Keep in sync with the parent row (SSE task updates carry fresh docs).
  useEffect(() => {
    if (Array.isArray(initialDocs)) setItems(initialDocs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDocs]);

  // Mount fetch only when the caller had no docs array at all (older cached
  // rows); include-bearing GET/SSE payloads make this a no-op.
  useEffect(() => {
    if (!persisted || Array.isArray(initialDocs)) return;
    let cancelled = false;
    setLoading(true);
    _list(parentId)
      .then((rows) => {
        if (!cancelled) setItems(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentId]);

  const apply = (next: any) => {
    setItems(next);
    if (persisted) {
      if (typeof onDocsChange === "function") onDocsChange(next);
    } else if (typeof onLocalChange === "function") onLocalChange(next);
  };

  async function add() {
    if (!canWrite) return;
    const url = draft.link.trim();
    if (!url) return;
    const label = draft.name.trim();
    if (persisted) {
      try {
        // The two creators take different parent keys, which is the whole
        // difference between them — so the branch is on the CALL, not on a
        // union payload a single signature would have to accept both halves of.
        const row = isEvent
          ? await createAgendaEventDoc({ eventId: parentId, label: label || null, url, ...draftClassify(draft) })
          : await createTaskDoc({ taskId: parentId, label: label || null, url, ...draftClassify(draft) });
        /* (v) — the doc exists (the await above answered). 151C §6: the pen
           is the purple-link reconcile (its own store, its own POST), so the
           s()-engine ordering note that stood here is retired with the
           stageId pen — nothing below shares an array with this write. */
        writeStageLink("add");
        apply([...items, row]);
        setDraft({ name: "", link: "" });
        setAddOpen(false); // §1c — on add, close; a failed create keeps it open
      } catch (e: any) {
        console.warn((isEvent ? "Event" : "Task") + " doc create failed:", e.message);
        // Phase 30 (D-3) — surface the failure (was console-only).
        window.__lxToast?.("Document: " + (e?.message || "save failed"), "error");
      }
    } else {
      writeStageLink("add"); // 151C §6 — a no-op in local mode (no parent id, nothing to link)
      apply([
        ...items,
        { _localId: `tmp-${Date.now()}-${items.length}`, label: label || null, url, ...draftClassify(draft) },
      ]);
      setDraft({ name: "", link: "" });
      setAddOpen(false); // §1c — on add, close
    }
  }

  const closeDialog = () => {
    setAddOpen(false);
    setEditRow(null);
    setDraft({ name: "", link: "" });
  };

  // 109 §1b — Save on the edit variant: PATCH in place for persisted rows,
  // a local map for the create-modal's unsaved rows. A failed PATCH keeps the
  // dialog open, like a failed create does.
  async function saveEdit() {
    if (!canWrite || !editRow) return;
    const url = draft.link.trim();
    if (!url) return;
    const label = draft.name.trim();
    if (persisted && editRow.id) {
      try {
        const row = (isEvent
          ? await updateAgendaEventDoc(editRow.id, { label: label || null, url, ...draftClassify(draft) })
          : await updateTaskDoc(editRow.id, { label: label || null, url, ...draftClassify(draft) })) as Record<string, unknown>;
        writeStageLink("edit"); // (vi) — a change moves the purple link, Unlink's clear removes it
        apply(items.map((x) => (x.id === editRow.id ? { ...x, ...row } : x)));
        closeDialog();
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        console.warn((isEvent ? "Event" : "Task") + " doc update failed:", msg);
        window.__lxToast?.("Document: " + (msg || "save failed"), "error");
      }
    } else {
      writeStageLink("edit"); // 151C §6 — no-op in local mode
      apply(
        items.map((x) =>
          x === editRow || (x._localId && x._localId === editRow._localId)
            ? { ...x, label: label || null, url, ...draftClassify(draft) }
            : x,
        ),
      );
      closeDialog();
    }
  }

  /* 112 §6 (RULED, Laurent's words) — document links stop being archivable:
     meaningless without their parent task/event, so no Archive section for
     them — rightly so. The archive toggle is REPLACED by the bin: an
     effective DELETE in the house removal shape (no confirm, row gone, 5s
     Undo re-creates from the snapshot via the ordinary POST — a new id, per
     the pattern). Server side this slice ADDED the DELETE routes (taskDocs /
     agendaEventDocs — the classes had only archive-flag routes), same write
     gate as their siblings, chronicled 'purge', out-of-scope 404. Rows
     ALREADY archived stay dormant: the map skips them, nothing sweeps them.
     Local (unsaved) rows just get removed, as before. */
  const { confirmAndUndo, undoToast, runUndo } = useConfirmAndUndo();
  const itemsRef = useRef(items);
  itemsRef.current = items;
  function binDelete(idx: number) {
    if (!canWrite) return;
    const cur = items[idx];
    if (!cur) return;
    if (!persisted || !cur.id) {
      apply(items.filter((_, i) => i !== idx));
      return;
    }
    confirmAndUndo({
      label: null,
      toastName: t("task.docs.deleted_toast", { fallback: "Document link deleted" }),
      onConfirm: async () => {
        try {
          await (isEvent ? deleteAgendaEventDoc(cur.id) : deleteTaskDoc(cur.id));
          apply(items.filter((_, i) => i !== idx));
        } catch (e) {
          const msg = e instanceof Error ? e.message : "delete failed";
          console.warn((isEvent ? "Event" : "Task") + " doc delete failed:", msg);
          window.__lxToast?.("Document: " + msg, "error");
        }
      },
      undo: {
        snapshot: { ...cur },
        restore: async (snap: DocRowShape) => {
          try {
            /* 151C §5 — the re-create carries the whole classification, or
               the undo would resurrect a declassified row. */
            const fields = {
              fileType: snap.fileType || null,
              date: snap.date || null,
              type: snap.type || null,
              status: snap.status || null,
              note: snap.note || null,
            };
            const row = isEvent
              ? await createAgendaEventDoc({ eventId: parentId as number, label: snap.label || null, url: String(snap.url || ""), ...fields })
              : await createTaskDoc({ taskId: parentId, label: snap.label || null, url: String(snap.url || ""), ...fields });
            apply([...itemsRef.current, row]);
          } catch (e) {
            window.__lxToast?.("Document: " + (e instanceof Error ? e.message : "restore failed"), "error");
          }
        },
      },
    });
  }

  return (
    /* BRIEF 95 §3a(d) — THE `embedded` FORK IS GONE, AND THE MODAL'S SHAPE IS
       THE ONE THAT SURVIVED.
       §3a(d) offered two ways: the drawer passes `embedded`, or the fork goes.
       The fork goes, because with four mounts the prop only ever had one
       correct value and nothing said so — `.field` was a variant nobody chose,
       it was a variant three call sites forgot.
       WHAT IT WAS ACTUALLY DOING, measured on screen rather than read off the
       prop's name: `.field` carries `App.css:981`'s `text-transform: uppercase`
       on its label, so the MODAL's heading read "DOCUMENT LINKS" and the
       drawer's read "Document links" — while "SUBTASKS", four pixels above it
       in that same drawer, read uppercase. The drawer disagreed with the modal
       AND with its own neighbour. It has never been about a tinted wrapper:
       the tint is the CALLER's (Dashboard.tsx:2281) and `embedded = false` gave
       this element nothing at all.
       So every mount now gets the section chrome, and the empty-state span's
       `textTransform: "none"` below (brief 90 §1a) is what keeps the one line
       that must NOT shout from shouting. */
    <div className="field" style={{ paddingTop: 8, marginTop: 6 }}>
      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          /* 216 addendum 7 §11 (2) [RULED 84] — as small as « Sous-tâches »:
             the field label's 9,5 px (SubtaskChecklist.tsx, NotesDrawer.tsx —
             the three drawers' headings one size, wherever they open). */
          fontSize: FS.s9_5,
          fontWeight: 600,
          color: "var(--ts)",
        }}
      >
        {/* BRIEF 87 §6 E5 / §0(5) — "Document links", in BOTH modals. A NEW KEY
            and not an edit of `task.docs.section_label`: that row is already
            seeded on every box, and `seed_deploy_translations.js` is
            insert-if-missing (createMany + skipDuplicates), so changing its text
            in the file corrects nothing in any database — 82g's finding, and
            86d's the week after. A new key is the only change that actually
            reaches a running server. */}
        {t("docs.section_label_links", { fallback: "Document links" })}
        {loading && (
          <span style={{ fontSize: FS.s9, color: "var(--tm)" }}>
            {t("common.loading", { fallback: "loading…" })}
          </span>
        )}
        {/* BRIEF 90 §1a — the empty-state moves ONTO the heading row, far right,
            exactly as the subtasks one does. It sat under the heading and cost a
            line of vertical space on the state that has nothing else to show.

            `textTransform: "none"` is not decoration and is the same correction
            §1c makes next door: this label is a `.field label`, and
            `App.css:949` shouts everything inside one. The line was lower case
            where it used to live, and moving it into the heading is what would
            have made it uppercase. Same size, same colour, same case as the
            subtasks line — which is the whole point of putting them in the same
            place. */}
        {items.length === 0 && !loading && (
          <span
            style={{
              marginLeft: "auto",
              fontSize: FS.s10,
              color: "var(--tm)",
              fontStyle: "italic",
              fontWeight: 400,
              textTransform: "none",
              letterSpacing: 0,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {t("task.docs.empty_state", { fallback: "No documents yet." })}
          </span>
        )}
        {/* BRIEF 108 §1b — the add affordance on the heading's own line,
            right-aligned, under EXACTLY the predicate that rendered the
            permanent add row (`canWrite`). Same margin dance as the subtasks
            heading: the empty-state hint owns the auto margin when present.
            BRIEF 117 §7 — `&& !locked`. 116 §1 put the lock back on the SUBTASK
            add and measured that this one gated on rights alone, so one locked
            drawer stack offered no add under Subtasks and a "+ Add" under
            Document links. The bin four screens down (`canWrite && !locked`,
            :449, 112 §6) already carried the term this line did not — the
            inconsistency was inside one file. */}
        {canWrite && !locked && (
          <AddButton
            label={t("common.add", { fallback: "Add" })}
            onClick={() => {
              /* 151C §6 — the select opens on the TRUTH: the parent's
                 current PURPLE link, so an untouched add is a no-op write. */
              if (stageLink)
                setDraft({ name: "", link: "", stageId: linkedStageId });
              setAddOpen(true);
            }}
            style={{
              flexShrink: 0,
              ...(items.length === 0 && !loading ? {} : { marginLeft: "auto" }),
            }}
          />
        )}
      </label>
      {items.map((d, i) => {
        const isArch = !!d.archived;
        /* 112 §6 — archived rows render NOWHERE live: the dormant legacy
           rows are skipped, not greyed. Indices stay aligned with `items`
           because the map itself is unchanged. */
        if (isArch) return null;
        const text = d.label || d.url;
        /* ── 151C §8 — THE DRAWER ROW, the stage row's contract on the task/
           event side. (a) the FILE-TYPE badge leads the row (stored fileType,
           else derived off the name/url; a message: URI wears the email
           badge); the generic lucide icon serves only the drawer toggle now.
           (b) clicking ANYWHERE on the row opens the LINK, in BOTH lock
           states — 109 §1b's unlocked mode had replaced the open with the
           edit dialog, which is what "opens the modal and the link cannot be
           opened at all" reported; nothing swallowed the stage row's
           openDocHref (measured: no ancestor handler — the swallow was THIS
           pane's mode fork). (c) editing moves to the usual pencil beside
           the bin, unlocked only. */
        const ftI =
          (FT_INFO as Record<string, { l: string; c: string }>)[
            isEmailDocUrl(d.url || "")
              ? "email"
              : d.fileType || detFT(d.label || d.url || "")
          ] || (FT_INFO as Record<string, { l: string; c: string }>).other;
        return (
          <div
            key={d.id || d._localId || i}
            onClick={() => {
              /* 126 §9 — THE EMAIL ROW RESOLVES ON DEMAND. A `message:` URI
                 is the Message-ID the drop was handed; `openDocHref` refuses
                 it by design and is NOT widened (lib/emailLink.ts carries
                 every refusal 125 §4's retired button used to say). */
              if (isEmailDocUrl(d.url)) {
                void openEmailDocHref(d.url, t);
                return;
              }
              openDocHref(d.url);
            }}
            title={t("case.doc.open", { fallback: "Open document" })}
            style={{
              display: "flex",
              gap: 6,
              alignItems: "center",
              padding: "3px 0",
              cursor: "pointer",
            }}
          >
            <DocTypeBadge ftI={ftI} />
            <span
              title={d.label ? `${d.label} — ${d.url}` : d.url}
              style={{
                flex: 1,
                minWidth: 40,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: FS.s11,
              }}
            >
              {text}
            </span>
            {/* (c) pencil beside the bin, both unlocked only (112 §6's lock
                term on the bin, now shared): absent when locked and absent
                in the Dashboard's view-only drawers (canWrite false). */}
            {canWrite && !locked && (
              <span
                style={{ display: "flex", gap: 3, flexShrink: 0 }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  className="btn btn-ghost"
                  style={{ padding: "1px 4px", fontSize: FS.s9 }}
                  title={t("task.docs.edit_tip", { fallback: "Edit document link" })}
                  onClick={() => {
                    setDraft({
                      name: d.label || "",
                      link: d.url || "",
                      /* 151C §5 — the stored classify fields prefill the
                         edit; source derives off fileType (the stage host's
                         own mapping) so the date label reads right. A legacy
                         row holds none and prefills exactly as before. */
                      fileType: d.fileType || undefined,
                      date: d.date || "",
                      source:
                        d.fileType === "weblink"
                          ? "weblink"
                          : d.fileType
                            ? "casefile"
                            : undefined,
                      type: d.type || undefined,
                      status: d.status || undefined,
                      note: d.note || undefined,
                      /* 151C §6 — the doc's stage is the parent's PURPLE
                         link; the edit dialog shows it and (vi) fires only
                         on a change. */
                      ...(stageLink ? { stageId: linkedStageId } : {}),
                    });
                    setEditRow(d);
                    setAddOpen(true);
                  }}
                >
                  <Pencil size={I.s} />
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  style={{ padding: "1px 4px", fontSize: FS.s9 }}
                  title={t("task.docs.delete_tip", {
                    fallback: "Delete document link",
                  })}
                  onClick={() => binDelete(i)}
                >
                  <Trash2 size={I.s} />
                </button>
              </span>
            )}
          </div>
        );
      })}
      {/* BRIEF 108 §1a/§1c — the permanent add row is gone; adding goes
          through a small dialog carrying the SAME controls the row had
          (label · web link · OneDrive) and the same add(): Enter in the link
          field still adds, a failed create keeps the dialog open. On add:
          close (§1c — one add per opening); on cancel: the draft is
          discarded. 110 §1a — OneDrive is the pane's own button now (the
          stage drawer's model), so a pick just fills the draft and every
          mode of this dialog can browse. 92 §3d's mutedField stays behind
          deliberately: a dialog's draft is live work, and with both add rows
          gone the const lost its last consumer and left with them. */}
      {/* 151 §2 — 108 §1a's dialog is now the ONE DocumentEntryModal; every
          contract it carried travels as a prop: the LIFTED draft (the modal's
          own upload fills it — 151 §3 moved that machinery in), the edit
          variant with its ghost Open (109 §1b — keeping this host's email/doc
          href fork, 126 §9), the fill-not-create OneDrive arm (110 §1a), and
          `upload` (127 §1's control, rendered and run by the modal now). */}
      {canWrite && addOpen && (
        <DocumentEntryModal
          t={t}
          editing={!!editRow}
          onClose={closeDialog}
          onSave={editRow ? saveEdit : add}
          onOpen={(link) => {
            const _u = link || (editRow ? editRow.url : "");
            if (isEmailDocUrl(_u)) {
              void openEmailDocHref(_u, t);
              return;
            }
            openDocHref(_u);
          }}
          oneDrive={oneDrive}
          /* 151C §5 — the RICH face from this entry point too (RULED: one
             modal means one face; the store learned the fields). The two
             lists are the pane's own subscriptions above. */
          classify={{ docTypesList: _docTypesList, docStatusesList: _docStatusesList }}
          /* 151 §2 step 4 — (v)/(vi): the Link to Stage select, optional
             here (a task doc may carry no stage; clearing is (vi)'s remove).
             Labels resolve through the pane's own case_stage subscription. */
          stageLink={
            stageLink
              ? {
                  stages: stageLink.stages,
                  entries: _stageEntries,
                  required: false,
                }
              : null
          }
          draft={draft}
          setDraft={setDraft}
          /* no pickFolder: the task/event quick-attach keeps 127 §1's ruled
             root filing — a folder dialog would make attaching one document
             a three-dialog act */
          upload={{}}
        />
      )}
      {/* 112 §6 — the bin's 5s Undo toast (house removal shape). */}
      <UndoToast toast={undoToast} onUndo={runUndo} />
    </div>
  );
}
